import type { MediaItem, MediaSource, TravelLog } from './types'
import { cleanString } from './utils'

// const LEGACY_VISITS_KEY = 'been-web:visits'
const ITEMS_KEY = 'been-web:items'
const DB_NAME = 'been-web'
const DB_VERSION = 2
const MEDIA_STORE = 'media'
// const LEGACY_PHOTO_STORE = 'photos'


export function loadTravelLogs(): TravelLog[] {
  try {
    const raw = JSON.parse(localStorage.getItem(ITEMS_KEY) ?? 'null')

    if (Array.isArray(raw)) {
      return (raw as Array<Record<string, unknown>>).map(migrateItem)
    }
  } catch {
    console.error('Error while loading travel logs')
  }

  return []
}




// ---- localStorage (items) ----------------------------------------------------

/**
 * 
 * @returns {Item[]}
 */
// export function loadItems(): Item[] {
//   try {
//     const raw = JSON.parse(localStorage.getItem(ITEMS_KEY) ?? 'null')
//     if (Array.isArray(raw)) {
//       return (raw as Array<Record<string, unknown>>).map(migrateItem)
//     }
//   } catch {
//     // fall through to legacy migration
//   }
//   return migrateLegacyVisits()
// }

export function saveItems(items: TravelLog[]): void {
  localStorage.setItem(ITEMS_KEY, JSON.stringify(items))
}

/**
 * Brings any stored item shape into the current `Item` schema:
 *  - already-new items (with `media: MediaItem[]`) pass through.
 *  - pre-multi-media items (with `mediaId`/`mediaSource`/`mediaUrl`) are
 *    wrapped into a single-element `media` array.
 */
export function migrateItem(raw: Record<string, unknown>): TravelLog {
  const media: MediaItem[] =
    // Array.isArray(raw.media)
    (raw.media as MediaItem[])
  // : legacySingleMediaToArray(raw)
  return {
    id: cleanString(raw.id) || crypto.randomUUID(),
    // kind: normalizeKind(raw.kind),
    title: cleanString(raw.title),
    note: cleanString(raw.note),
    country: cleanString(raw.country),
    lat: Number(raw.lat),
    lng: Number(raw.lng),
    visitedAt: cleanString(raw.visitedAt),
    media,
    createdAt: cleanString(raw.createdAt) || new Date().toISOString(),
  }
}

// function legacySingleMediaToArray(raw: Record<string, unknown>): MediaItem[] {
//   const source = cleanString(raw.mediaSource) as MediaSource | ''
//   const mediaId = cleanString(raw.mediaId)
//   const mediaUrl = cleanString(raw.mediaUrl)
//   const mediaType = cleanString(raw.mediaType)
//   if (source === 'indexeddb' && mediaId) {
//     return [
//       {
//         id: crypto.randomUUID(),
//         source: 'indexeddb',
//         type: mediaType,
//         url: '',
//         blobId: mediaId,
//       },
//     ]
//   }
//   if (source === 'url' && mediaUrl) {
//     return [
//       {
//         id: crypto.randomUUID(),
//         source: 'url',
//         type: mediaType,
//         url: mediaUrl,
//         blobId: '',
//       },
//     ]
//   }
//   return []
// }

// function normalizeKind(value: unknown): Item['kind'] {
//   const k = cleanString(value)
//   return k === 'photo' || k === 'video' ? k : 'travel-log'
// }

// function migrateLegacyVisits(): Item[] {
//   try {
//     const visits = JSON.parse(
//       localStorage.getItem(LEGACY_VISITS_KEY) ?? '[]',
//     ) as Array<Record<string, unknown>>
//     if (!Array.isArray(visits)) return []
//     const items: Item[] = visits.flatMap((visit) => {
//       const baseId = cleanString(visit.id) || crypto.randomUUID()
//       const log: Item = {
//         id: baseId,
//         kind: 'travel-log',
//         title:
//           cleanString(visit.city) ||
//           cleanString(visit.country) ||
//           'Travel log',
//         note: cleanString(visit.note),
//         country: cleanString(visit.country),
//         lat: Number(visit.lat),
//         lng: Number(visit.lng),
//         visitedAt: cleanString(visit.visitedAt),
//         media: [],
//         createdAt: cleanString(visit.createdAt) || new Date().toISOString(),
//       }
//       const photoId = cleanString(visit.photoId)
//       const photo: Item | null = photoId
//         ? {
//           ...log,
//           id: `${baseId}-photo`,
//           kind: 'photo',
//           title: cleanString(visit.city) || 'Photo',
//           media: [
//             {
//               id: crypto.randomUUID(),
//               source: 'indexeddb',
//               type: 'image/jpeg',
//               url: '',
//               blobId: photoId,
//             },
//           ],
//         }
//         : null
//       return photo ? [log, photo] : [log]
//     })
//     saveItems(items)
//     return items
//   } catch {
//     return []
//   }
// }

// ---- IndexedDB (media blobs) -------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(MEDIA_STORE)) {
        db.createObjectStore(MEDIA_STORE)
      }
      // if (!db.objectStoreNames.contains(LEGACY_PHOTO_STORE)) {
      //   db.createObjectStore(LEGACY_PHOTO_STORE)
      // }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function saveMedia(id: string, blob: Blob): Promise<void> {
  const db = await openDb()
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(MEDIA_STORE, 'readwrite')
    transaction.objectStore(MEDIA_STORE).put(blob, id)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

export async function getMedia(id: string): Promise<Blob | null> {
  const db = await openDb()
  return new Promise<Blob | null>((resolve, reject) => {
    const transaction = db.transaction([MEDIA_STORE])
    const mediaRequest = transaction.objectStore(MEDIA_STORE).get(id)
    mediaRequest.onsuccess = () => {
      const result = mediaRequest.result as Blob | undefined
      if (result) {
        resolve(result)
        return
      }
    }
    mediaRequest.onerror = () => reject(mediaRequest.error)
  })
}

export async function deleteMedia(id: string): Promise<void> {
  const db = await openDb()
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(MEDIA_STORE, 'readwrite')
    transaction.objectStore(MEDIA_STORE).delete(id)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

// ---- Blob <-> data URL (used by backup import/export) ------------------------

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

/**
 * Decode a data URL into a Blob without `fetch()`. `fetch(dataUrl)` rejects on
 * very large data: URIs (browser data-URI size caps, esp. WebKit/iOS), which
 * would abort a whole backup import over a single big video. Manual base64
 * decode has no such cap.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',')
  if (comma === -1) throw new Error('malformed data url')
  const header = dataUrl.slice(0, comma)
  const mime = /^data:([^;,]*)/.exec(header)?.[1] || 'application/octet-stream'
  const payload = dataUrl.slice(comma + 1)
  if (!header.includes(';base64')) {
    return new Blob([decodeURIComponent(payload)], { type: mime })
  }
  const binary = atob(payload)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}
